import { useState, useMemo, useCallback, useEffect } from 'react'
import { Plus, Trash2, CheckCircle2, AlertTriangle, XCircle, ListChecks } from 'lucide-react'
import { useAntarcticaDeals, useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import { comparisonInputs, computeMetrics, type ComputedMetrics, type ScenarioData } from '@/pages/loanComparisonMetrics'
import { toMillions } from '@/pages/liveModuleData'
import { RATE_LABELS, sofrOf, useLiveRates } from '@/services/marketData'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = {
  money: (v: number) => `$${v.toFixed(1)}M`,
  pct: (v: number) => `${(v * 100).toFixed(1)}%`,
  x: (v: number) => `${v.toFixed(2)}x`,
  bps: (v: number) => `SOFR+${v}`,
  ds: (v: number) => `$${v.toFixed(2)}M`,
}

/**
 * Standard templates at 65 / 72 / 80% of the recorded value. With no value on
 * record there is no LTV to size by (R3: the stated LTV never back-solves
 * value), so each template starts at the deal's stated loan and LTV is unknown.
 */
function defaultScenarios(propertyValue: number, statedLoan: number): ScenarioData[] {
  return templateScenarios(propertyValue).map((s) =>
    propertyValue > 0 ? s : { ...s, loanAmount: statedLoan > 0 ? +statedLoan.toFixed(1) : 0, ltv: 0 },
  )
}

function templateScenarios(propertyValue: number): ScenarioData[] {
  return [
    {
      label: 'Conservative',
      loanAmount: +(propertyValue * 0.65).toFixed(1),
      ltv: 0.65,
      product: 'Senior Mortgage',
      rate: 'SOFR+325',
      spreadBps: 325,
      term: '7 Year',
      ioPeriod: '24 months',
      amortization: '30-Year',
      prepay: '3-2-1',
      rateFloor: 1.0,
      extension: '1 x 12mo',
      recourse: 'Non-Recourse',
    },
    {
      label: 'Moderate',
      loanAmount: +(propertyValue * 0.72).toFixed(1),
      ltv: 0.72,
      product: 'Senior Mortgage',
      rate: 'SOFR+375',
      spreadBps: 375,
      term: '5 Year',
      ioPeriod: '12 months',
      amortization: '30-Year',
      prepay: '5-4-3-2-1',
      rateFloor: 1.0,
      extension: '2 x 12mo',
      recourse: 'Non-Recourse',
    },
    {
      label: 'Aggressive',
      loanAmount: +(propertyValue * 0.80).toFixed(1),
      ltv: 0.80,
      product: 'Sr + Mezz',
      rate: 'SOFR+425/+600',
      spreadBps: 425,
      term: '5 Year',
      ioPeriod: 'Full Term',
      amortization: 'Interest Only',
      prepay: 'Lockout 2yr',
      rateFloor: 1.5,
      extension: 'None',
      recourse: 'Partial Recourse',
    },
  ]
}

// ---------------------------------------------------------------------------
// Metric Row colors
// ---------------------------------------------------------------------------
function metricColor(value: number, thresholds: { green: number; amber: number }, higher: boolean) {
  if (higher) {
    if (value >= thresholds.green) return 'text-emerald-400'
    if (value >= thresholds.amber) return 'text-amber-300'
    return 'text-red-400'
  }
  if (value <= thresholds.green) return 'text-emerald-400'
  if (value <= thresholds.amber) return 'text-amber-300'
  return 'text-red-400'
}

function stressBadge(dscr: number | null) {
  if (dscr == null) return { label: 'n/a', color: 'border-stone-600/30 bg-white/[0.02] text-stone-500' }
  if (dscr >= 1.15) return { label: `${dscr.toFixed(2)}x PASS`, color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' }
  if (dscr >= 1.0) return { label: `${dscr.toFixed(2)}x WARN`, color: 'border-amber-400/30 bg-amber-400/10 text-amber-200' }
  return { label: `${dscr.toFixed(2)}x FAIL`, color: 'border-red-500/30 bg-red-500/10 text-red-300' }
}

function guidelineBadge(g: ComputedMetrics['guideline']) {
  if (g === 'unknown') return { label: 'Not computable', color: 'border-stone-600/30 bg-white/[0.02] text-stone-500' }
  if (g === 'in-box') return { label: 'In Box', color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' }
  if (g === 'stretch') return { label: 'Stretch', color: 'border-amber-400/30 bg-amber-400/10 text-amber-200' }
  return { label: 'Out of Box', color: 'border-red-500/30 bg-red-500/10 text-red-300' }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function LoanComparison() {
  const deals = useAntarcticaDeals('loan-comparison')
  const demoEnabled = useModuleDemoEnabled('loan-comparison')
  // Guarded for the empty live pipeline — see TermSheet for the same crash.
  const [selectedDealId, setSelectedDealId] = useState(deals[0]?.id ?? '')
  const deal = deals.find((d) => d.id === selectedDealId) ?? deals[0]

  const { propertyValue, noi, valueSource } = comparisonInputs(deal)
  const statedLoan = toMillions(deal?.loanAmount)

  // Debt service needs today's SOFR. Live reads the NY Fed print; the demo
  // keeps its illustrative 4.30%. It used to be a silent 4.3% assumption.
  const rates = useLiveRates(!demoEnabled)
  const liveSofr = sofrOf(rates.data)
  const sofrRate = demoEnabled ? 0.043 : liveSofr?.rate ?? null

  const [scenarios, setScenarios] = useState<ScenarioData[]>(() => defaultScenarios(propertyValue, statedLoan))
  // Scenarios were seeded once at mount — from an empty deal list on a cold
  // load — so every loan amount stayed 0 and DSCR divided by zero. Re-seed
  // whenever the selected deal's inputs change.
  const seedKey = `${deal?.id ?? ''}:${propertyValue}:${noi}:${statedLoan}`
  const [seededFor, setSeededFor] = useState(seedKey)
  useEffect(() => {
    if (seededFor === seedKey) return
    setSeededFor(seedKey)
    setScenarios(defaultScenarios(propertyValue, statedLoan))
  }, [seedKey, seededFor, propertyValue, statedLoan])

  const updateScenario = useCallback((index: number, field: keyof ScenarioData, value: string | number) => {
    setScenarios((prev) => {
      const next = [...prev]
      const s = { ...next[index] }
      if (field === 'loanAmount') {
        const amt = typeof value === 'string' ? parseFloat(value) || 0 : value
        s.loanAmount = amt
        s.ltv = propertyValue > 0 ? amt / propertyValue : 0
      } else if (field === 'spreadBps') {
        const bps = typeof value === 'string' ? parseInt(value, 10) || 0 : value
        s.spreadBps = bps
        s.rate = `SOFR+${bps}`
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s as any)[field] = value
      }
      next[index] = s
      return next
    })
  }, [propertyValue])

  const addScenario = useCallback(() => {
    if (scenarios.length >= 4) return
    setScenarios((prev) => [
      ...prev,
      {
        label: `Scenario ${String.fromCharCode(65 + prev.length)}`,
        loanAmount: propertyValue > 0 ? +(propertyValue * 0.70).toFixed(1) : statedLoan > 0 ? +statedLoan.toFixed(1) : 0,
        ltv: propertyValue > 0 ? 0.70 : 0,
        product: 'Senior Mortgage',
        rate: 'SOFR+350',
        spreadBps: 350,
        term: '5 Year',
        ioPeriod: '12 months',
        amortization: '30-Year',
        prepay: '3-2-1',
        rateFloor: 1.0,
        extension: '1 x 12mo',
        recourse: 'Non-Recourse',
      },
    ])
  }, [scenarios.length, propertyValue, statedLoan])

  const removeScenario = useCallback((index: number) => {
    if (scenarios.length <= 2) return
    setScenarios((prev) => prev.filter((_, i) => i !== index))
  }, [scenarios.length])

  const handleDealChange = useCallback((id: string) => {
    setSelectedDealId(id)
  }, [])

  const computed = useMemo(
    () => scenarios.map((s) => computeMetrics(s, noi, propertyValue, sofrRate)),
    [scenarios, noi, propertyValue, sofrRate],
  )

  // Find best scenario (highest DSCR with guideline in-box or stretch)
  const bestIndex = useMemo(() => {
    let best = 0
    let bestScore = -Infinity
    computed.forEach((c, i) => {
      if (c.dscr == null) return
      const guidelineScore = c.guideline === 'in-box' ? 2 : c.guideline === 'stretch' ? 1 : 0
      const score = c.dscr * 10 + (c.cashOnCash ?? 0) * 0.5 + guidelineScore * 5
      if (score > bestScore) {
        bestScore = score
        best = i
      }
    })
    return best
  }, [computed])

  // A deterministic summary of the table, not a model's opinion. It claims a
  // stress cushion only when the stress rows actually pass.
  const recommendation = useMemo(() => {
    const best = scenarios[bestIndex]
    const bestC = computed[bestIndex]
    if (!best || !bestC || bestC.dscr == null) {
      if (!(noi > 0)) return 'Coverage cannot be computed: this deal records no NOI. Record NOI on the deal to compare scenarios.'
      if (sofrRate == null) return 'Coverage cannot be computed: the SOFR print is unavailable.'
      return 'Coverage cannot be computed: no scenario has a loan amount.'
    }
    if (bestC.guideline === 'out-of-box') {
      return `All scenarios are outside the guideline box. Consider reducing leverage or improving cash flow coverage before proceeding.`
    }
    const cushion =
      (bestC.stressDownside ?? 0) >= 1.15 && (bestC.stressSevere ?? 0) >= 1.0
        ? 'It passes both stress cases.'
        : (bestC.stressDownside ?? 0) >= 1.15
          ? `It passes the -15% NOI case but not the -30% case (${fmt.x(bestC.stressSevere ?? 0)}).`
          : `It does not pass the -15% NOI stress case (${fmt.x(bestC.stressDownside ?? 0)}).`
    const coc = bestC.cashOnCash != null ? ` and ${bestC.cashOnCash.toFixed(1)}% cash-on-cash` : ''
    const ltvText = best.ltv > 0 ? `${fmt.pct(best.ltv)} LTV` : 'LTV unknown (no value on record)'
    return `${best.label} (Scenario ${String.fromCharCode(65 + bestIndex)}) scores highest: ${ltvText} with ${fmt.x(bestC.dscr)} coverage${coc}. ${cushion}`
  }, [scenarios, computed, bestIndex, noi, sofrRate])

  // ---------------------------------------------------------------------------
  // Editable cell component
  // ---------------------------------------------------------------------------
  function EditableCell({ value, onChange, type = 'text', suffix }: { value: string | number; onChange: (v: string) => void; type?: string; suffix?: string }) {
    return (
      <div className="flex items-center gap-1">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-amber-400/12 bg-black/40 px-2 py-1 text-xs text-stone-200 outline-none transition-colors focus:border-amber-400/40 focus:ring-1 focus:ring-amber-400/20"
        />
        {suffix && <span className="text-[10px] text-stone-600">{suffix}</span>}
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Comparison rows definition
  // ---------------------------------------------------------------------------
  interface RowDef {
    label: string
    render: (s: ScenarioData, c: ComputedMetrics, i: number) => React.ReactNode
  }

  const rows: RowDef[] = [
    {
      label: 'Loan Amount',
      render: (s, _c, i) => (
        <EditableCell
          value={s.loanAmount}
          type="number"
          onChange={(v) => updateScenario(i, 'loanAmount', v)}
          suffix="M"
        />
      ),
    },
    {
      label: 'LTV',
      render: (s, _c) =>
        s.ltv > 0 ? (
          <span className={metricColor(s.ltv, { green: 0.70, amber: 0.78 }, false)}>{fmt.pct(s.ltv)}</span>
        ) : (
          <span className="text-stone-500" title="No property value on record, so LTV cannot be computed.">—</span>
        ),
    },
    {
      label: 'Product',
      render: (s, _c, i) => (
        <EditableCell value={s.product} onChange={(v) => updateScenario(i, 'product', v)} />
      ),
    },
    {
      label: 'Rate',
      render: (s, _c, i) => (
        <EditableCell
          value={s.spreadBps}
          type="number"
          onChange={(v) => updateScenario(i, 'spreadBps', v)}
          suffix="bps"
        />
      ),
    },
    {
      label: 'Term',
      render: (s, _c, i) => (
        <select
          value={s.term}
          onChange={(e) => updateScenario(i, 'term', e.target.value)}
          className="w-full rounded-md border border-amber-400/12 bg-black/40 px-2 py-1 text-xs text-stone-200 outline-none focus:border-amber-400/40"
        >
          <option value="3 Year">3 Year</option>
          <option value="5 Year">5 Year</option>
          <option value="7 Year">7 Year</option>
          <option value="10 Year">10 Year</option>
        </select>
      ),
    },
    {
      label: 'IO Period',
      render: (s, _c, i) => (
        <select
          value={s.ioPeriod}
          onChange={(e) => updateScenario(i, 'ioPeriod', e.target.value)}
          className="w-full rounded-md border border-amber-400/12 bg-black/40 px-2 py-1 text-xs text-stone-200 outline-none focus:border-amber-400/40"
        >
          <option>None</option>
          <option>12 months</option>
          <option>24 months</option>
          <option>36 months</option>
          <option>Full Term</option>
        </select>
      ),
    },
    {
      label: 'DSCR',
      render: (_s, c) => c.dscr == null ? <span className="text-stone-500">—</span> : <span className={`font-semibold ${metricColor(c.dscr, { green: 1.35, amber: 1.20 }, true)}`}>{fmt.x(c.dscr)}</span>,
    },
    {
      label: 'Debt Yield',
      render: (_s, c) => c.debtYield == null ? <span className="text-stone-500">—</span> : <span className={metricColor(c.debtYield, { green: 0.09, amber: 0.075 }, true)}>{fmt.pct(c.debtYield)}</span>,
    },
    {
      label: 'Annual DS',
      render: (_s, c) => <span className="font-mono text-stone-200">{c.annualDS == null ? '—' : fmt.ds(c.annualDS)}</span>,
    },
    {
      label: 'Cash-on-Cash',
      render: (_s, c) => c.cashOnCash == null ? <span className="text-stone-500">—</span> : <span className={metricColor(c.cashOnCash, { green: 14, amber: 10 }, true)}>{c.cashOnCash.toFixed(1)}%</span>,
    },
    {
      label: 'Prepay',
      render: (s, _c, i) => (
        <EditableCell value={s.prepay} onChange={(v) => updateScenario(i, 'prepay', v)} />
      ),
    },
    {
      label: 'Guideline',
      render: (_s, c) => {
        const badge = guidelineBadge(c.guideline)
        return (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${badge.color}`}>
            {c.guideline === 'in-box' ? <CheckCircle2 size={10} /> : c.guideline === 'stretch' ? <AlertTriangle size={10} /> : c.guideline === 'unknown' ? null : <XCircle size={10} />}
            {badge.label}
          </span>
        )
      },
    },
  ]

  const stressRows = [
    {
      label: 'Downside (-15%)',
      render: (_s: ScenarioData, c: ComputedMetrics) => {
        const badge = stressBadge(c.stressDownside)
        return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.color}`}>{badge.label}</span>
      },
    },
    {
      label: 'Severe (-30%)',
      render: (_s: ScenarioData, c: ComputedMetrics) => {
        const badge = stressBadge(c.stressSevere)
        return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.color}`}>{badge.label}</span>
      },
    },
  ]

  if (!deal) {
    return (
      <div className="space-y-3 p-4">
        <div className="terminal-kicker">Loan Comparison</div>
        <div className="panel">
          <div className="panel-body px-4 py-10 text-center">
            <p className="text-[12px] text-stone-400">No deals available to compare.</p>
            <p className="mt-1 text-[11px] text-stone-600">
              Add a deal from Intake — email, file upload, or manual entry — and it will appear here.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-fill gap-2 p-3">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between">
        <div>
          <div className="terminal-kicker">Loan Comparison Tool</div>
          <p className="mt-1 text-xs text-stone-500">
            Side-by-side deal structure analysis with stress testing. Scenarios start from standard templates at
            65 / 72 / 80% of value; every term is editable.
          </p>
        </div>
        <button
          onClick={addScenario}
          disabled={scenarios.length >= 4}
          className="btn-secondary flex items-center gap-1.5 disabled:opacity-40"
        >
          <Plus size={13} />
          Add Scenario
        </button>
      </div>

      {/* Property Selector */}
      <div className="card flex-shrink-0 px-4 py-2">
        <div className="flex items-center gap-4">
          <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-600">Property</label>
          <select
            value={selectedDealId}
            onChange={(e) => handleDealChange(e.target.value)}
            className="input flex-1 max-w-md"
          >
            {deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.id} - {d.property} ({d.location}) - {d.type}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-4 text-xs text-stone-400">
            <span>
              Value: <strong className="text-stone-200">{propertyValue > 0 ? fmt.money(propertyValue) : '—'}</strong>
              {valueSource === 'none' && <span className="ml-1 text-[10px] text-stone-500">not recorded</span>}
              {valueSource === 'derived' && <span className="ml-1 text-[10px] text-violet-300">derived</span>}
            </span>
            <span>NOI: <strong className="text-stone-200">{noi > 0 ? fmt.money(noi) : '—'}</strong>{!(noi > 0) && <span className="ml-1 text-[10px] text-stone-500">not recorded</span>}</span>
            <span>Cap Rate: <strong className="text-stone-200">{noi > 0 && propertyValue > 0 ? fmt.pct(noi / propertyValue) : '—'}</strong></span>
            <span>
              SOFR:{' '}
              <strong className="text-stone-200">{sofrRate != null ? fmt.pct(sofrRate) : '—'}</strong>
              <span className="ml-1 text-[10px] text-stone-500">
                {demoEnabled
                  ? 'sample'
                  : liveSofr
                    ? `${RATE_LABELS.SOFR_ON.source}${liveSofr.asOf ? `, ${liveSofr.asOf}` : ''}`
                    : rates.isLoading ? 'loading' : 'unavailable'}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="card flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
          <table className="w-full">
            {/* Header */}
            <thead>
              <tr className="border-b border-amber-400/10">
                <th className="w-36 px-4 py-3 text-left text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 bg-black/20">Metric</th>
                {scenarios.map((s, i) => (
                  <th key={i} className="px-4 py-3 text-left bg-black/20">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className={`text-xs font-semibold ${i === bestIndex ? 'text-amber-300' : 'text-stone-200'}`}>
                          Scenario {String.fromCharCode(65 + i)}
                        </div>
                        <div className="text-[10px] text-stone-500">{s.label}</div>
                      </div>
                      {scenarios.length > 2 && (
                        <button onClick={() => removeScenario(i)} className="rounded p-0.5 text-stone-600 hover:text-red-400 transition-colors">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Main metrics */}
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b border-amber-400/6 hover:bg-white/[0.015] transition-colors">
                  <td className="px-4 py-2.5 text-[11px] font-medium text-stone-400">{row.label}</td>
                  {scenarios.map((s, i) => (
                    <td key={i} className="px-4 py-2.5 text-xs">
                      {row.render(s, computed[i], i)}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Stress test separator */}
              <tr>
                <td colSpan={scenarios.length + 1} className="px-4 py-2 bg-black/20">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-600">Stress Test Results</div>
                </td>
              </tr>

              {stressRows.map((row) => (
                <tr key={row.label} className="border-b border-amber-400/6 hover:bg-white/[0.015] transition-colors">
                  <td className="px-4 py-2.5 text-[11px] font-medium text-stone-400">{row.label}</td>
                  {scenarios.map((s, i) => (
                    <td key={i} className="px-4 py-2.5 text-xs">
                      {row.render(s, computed[i])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scenario summary — deterministic, from the table above */}
      <div className="card flex-shrink-0 px-4 py-2.5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg border border-amber-400/20 bg-amber-400/[0.06] p-1.5">
            <ListChecks size={14} className="text-amber-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="terminal-kicker">Scenario Summary</span>
              <span className="rounded-full border border-stone-600/40 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-stone-400">Rule-based · not AI</span>
            </div>
            <p className="mt-1 text-[12px] leading-5 text-stone-300">{recommendation}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
